/**
 * SearchModal — unified site search (command-palette style).
 *
 * Opens via the header search button (dispatches "ek-open-search") or the
 * Cmd/Ctrl+K shortcut. Queries GET /api/public/search and renders grouped
 * results (Insights / Services / Help). The ERIO bucket is wired into the
 * contract but currently returns empty — a "Connecting soon" note is shown so
 * the unified surface is visible the moment ERIO ships its search endpoint.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Layers, LifeBuoy, ArrowUpRight } from "lucide-react";
import api from "../lib/api";

const GROUP_META = {
  insights: { label: "Insights", icon: FileText },
  services: { label: "Services", icon: Layers },
  help: { label: "Help", icon: LifeBuoy },
};

export default function SearchModal() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);
  const navigate = useNavigate();

  const close = useCallback(() => { setOpen(false); setQ(""); setResults(null); }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("ek-open-search", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("ek-open-search", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get(`/public/search`, { params: { q } })
        .then(({ data }) => setResults(data))
        .catch(() => setResults(null))
        .finally(() => setLoading(false));
    }, 220);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, open]);

  const go = (url) => { close(); navigate(url); };

  if (!open) return null;

  const buckets = results?.results || {};
  const ordered = ["insights", "services", "help"].filter((k) => (buckets[k] || []).length > 0);
  const hasResults = ordered.length > 0;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/60 backdrop-blur-sm px-4 pt-[12vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
      data-testid="search-modal"
    >
      <div className="w-full max-w-2xl bg-white shadow-2xl border border-zinc-200">
        <div className="flex items-center gap-3 border-b border-zinc-200 px-4">
          <Search size={18} className="text-[#7737F6] shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search insights, services and help…"
            className="flex-1 py-4 text-[15px] text-black outline-none placeholder:text-zinc-400"
            data-testid="search-modal-input"
          />
          <button type="button" onClick={close} aria-label="Close search" className="text-zinc-400 hover:text-black" data-testid="search-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <p className="px-4 py-8 text-center text-zinc-400 text-sm">Type at least 2 characters to search.</p>
          ) : loading ? (
            <p className="px-4 py-8 text-center text-zinc-400 text-sm" data-testid="search-modal-loading">Searching…</p>
          ) : !hasResults ? (
            <p className="px-4 py-8 text-center text-zinc-500 text-sm" data-testid="search-modal-empty">
              No results for &ldquo;{q}&rdquo;. Try a different term.
            </p>
          ) : (
            ordered.map((key) => {
              const Meta = GROUP_META[key];
              const Icon = Meta.icon;
              return (
                <div key={key} className="py-2" data-testid={`search-group-${key}`}>
                  <div className="px-4 py-1.5 text-[10.5px] tracking-[0.18em] uppercase text-zinc-400 inline-flex items-center gap-1.5">
                    <Icon size={11} /> {Meta.label}
                  </div>
                  {buckets[key].map((r) => (
                    <button
                      key={r.url}
                      type="button"
                      onClick={() => go(r.url)}
                      className="w-full text-left px-4 py-2.5 hover:bg-[#F2F2F3] flex items-start gap-3 group"
                      data-testid="search-result-item"
                    >
                      <div className="flex-1">
                        <div className="text-[14px] font-medium text-black group-hover:text-[#7737F6]">{r.title}</div>
                        {r.snippet && <div className="text-[12.5px] text-zinc-500 mt-0.5 leading-snug">{r.snippet}</div>}
                      </div>
                      <ArrowUpRight size={14} className="text-zinc-300 group-hover:text-[#7737F6] mt-1 shrink-0" />
                    </button>
                  ))}
                </div>
              );
            })
          )}

          {/* ERIO unified-search stub */}
          {q.trim().length >= 2 && results && results.erio_status !== "connected" && (
            <div className="border-t border-zinc-100 px-4 py-3 text-[12px] text-zinc-400" data-testid="search-erio-stub">
              ERIO results will appear here once the sister platform is connected.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
